import {
  PATH_EDIT_CASE,
  PATH_EDIT_CASE_BY_ID,
  PATH_INBOX,
  PATH_INBOX_COMPLETED,
  PATH_INBOX_FLAGGED,
  PATH_NEW_CASE,
  PATH_NEW_CASE_FROM_XM,
  PATH_READONLY_CASE,
  PATH_SETTINGS,
} from '@/config/paths';
import useUserPreferences from '@/hooks/useUserPreferences';
import { ArrowLeft } from 'lucide-react';
import { Box } from '@mui/material';
import { grey } from '@mui/material/colors';
import { observer } from 'mobx-react';
import { NavLink, useLocation } from 'react-router-dom';

interface ISitemapItem {
  path: string;
  parent: string | null;
  title: string;
}

const getHomeSitemapItem = (homePath: string): ISitemapItem => {
  // Determine title based on which inbox page is the home
  let title = 'Indbakke';
  if (homePath === PATH_INBOX_FLAGGED) {
    title = 'Markerede';
  } else if (homePath === PATH_INBOX_COMPLETED) {
    title = 'Afsluttede';
  }
  
  return {
    path: homePath,
    parent: null,
    title,
  };
};

/**
 * Get sitemap for the top navigation.
 * Please note, we cannot use the React Router routes here because we don't use inheritance there.
 *
 * @param homePath
 * @returns
 */
const getSitemap = (homePath: string): ISitemapItem[] => {
  return [
    { path: PATH_INBOX, parent: null, title: 'Indbakke' },
    { path: PATH_INBOX_FLAGGED, parent: null, title: 'Markerede' },
    { path: PATH_INBOX_COMPLETED, parent: null, title: 'Afsluttede' },
    { path: PATH_NEW_CASE, parent: homePath, title: 'Ny sag' },
    { path: PATH_NEW_CASE_FROM_XM, parent: homePath, title: 'Ny sag' },
    { path: PATH_EDIT_CASE, parent: homePath, title: 'Rediger sag' },
    { path: PATH_EDIT_CASE_BY_ID, parent: homePath, title: 'Rediger sag' },
    { path: PATH_READONLY_CASE, parent: homePath, title: 'Sag (Skrivebeskyttet)' },
    { path: PATH_SETTINGS, parent: homePath, title: 'Indstillinger' },
  ];
};

/**
 * Check if a path matches a route pattern (e.g., /cases/123 matches /cases/:id)
 */
const matchesPattern = (path: string, pattern: string): boolean => {
  if (path === pattern) return true;
  
  const pathParts = path.split('/').filter(Boolean);
  const patternParts = pattern.split('/').filter(Boolean);
  
  if (pathParts.length !== patternParts.length) return false;
  
  return patternParts.every((part, i) => {
    return part.startsWith(':') || part === pathParts[i];
  });
};

const TopNavbar = observer(() => {
  const location = useLocation();
  const userPreferences = useUserPreferences();

  const currentPath = location.pathname;
  const homePath = userPreferences.lastInboxPath;
  const sitemap = getSitemap(homePath);

  const currentItem =
    sitemap.find((item) => matchesPattern(currentPath, item.path)) || getHomeSitemapItem(homePath);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'flex-start',
        alignItems: 'center',
        gap: 2,
      }}
    >
      {currentItem.parent !== null && (
        <NavLink
          to={currentItem.parent}
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'center',
            textDecoration: 'none',
            padding: '8px 12px',
            borderRadius: '8px',
            transition: 'all 0.2s ease',
            backgroundColor: 'transparent',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#f5f5f5';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          <ArrowLeft size={20} color={grey[700]} strokeWidth={2} />
        </NavLink>
      )}

      <Box
        className="dd-privacy-allow"
        sx={{
          fontStyle: 'normal',
          fontWeight: 500,
          fontSize: '20px',
          lineHeight: '133.4%',
          color: grey[900],
        }}
      >
        {currentItem.title}
      </Box>
    </Box>
  );
});

export default TopNavbar;
